import { useEffect, useState, useCallback } from "react";
import { api, fmt, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { NutritionEditor } from "@/components/NutritionEditor";
import { druhLabel, druhBadgeClass } from "@/lib/druh";
import { Search, LineChart as LineChartIcon } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const DruhBadge = ({ druh }) => (
  <Badge variant="outline" className={druhBadgeClass(druh)}>
    {druhLabel(druh)}
  </Badge>
);

export default function Materials() {
  const [data, setData] = useState({ materials: [], settings: null, total: 0 });
  const [search, setSearch] = useState("");
  const [druh, setDruh] = useState("__all__");
  const [category, setCategory] = useState("__all__");
  const [cats, setCats] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (druh !== "__all__") params.druh = druh;
    if (category !== "__all__") params.category = category;
    api.get("/materials", { params }).then((r) => setData(r.data));
  }, [search, druh, category]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data.categories || []));
  }, []);

  const openDetail = async (code) => {
    const r = await api.get(`/materials/${code}/history`);
    setDetail(r.data);
  };

  const base = data.settings?.price_base === "brutto" ? "Brutto" : "Netto";

  return (
    <div className="fade-up">
      <PageHeader title="Číselník surovin" subtitle={`${data.total} položek · ceny dle nastavení (${base})`} />
      <div className="px-10 py-8 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="materials-search" placeholder="Hledat surovinu nebo kód…"
              value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
          </div>
          <Select value={druh} onValueChange={setDruh}>
            <SelectTrigger className="w-48 bg-white" data-testid="materials-filter-druh"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Všechny druhy</SelectItem>
              {(data.druhy || []).map((d) => (
                <SelectItem key={d} value={d}>{druhLabel(d)} ({d})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-56 bg-white" data-testid="materials-filter-category"><SelectValue placeholder="Kategorie" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__all__">Všechny kategorie</SelectItem>
              {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/60 hover:bg-muted/60">
                <TableHead className="w-24">Kód</TableHead>
                <TableHead>Surovina</TableHead>
                <TableHead>Druh</TableHead>
                <TableHead>Kategorie</TableHead>
                <TableHead className="text-center">MJ</TableHead>
                <TableHead className="text-right">Poslední příjem</TableHead>
                <TableHead className="text-right">Vypočtená cena</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.materials.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  Žádné suroviny. Naimportujte Excel v sekci Import dat.
                </TableCell></TableRow>
              )}
              {data.materials.slice(0, 400).map((m) => (
                <TableRow key={m.code} data-testid={`material-row-${m.code}`}
                  onClick={() => openDetail(m.code)} className="cursor-pointer">
                  <TableCell className="num text-muted-foreground text-sm">{m.code}</TableCell>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell><DruhBadge druh={m.druh} /></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{m.category}</TableCell>
                  <TableCell className="text-center text-sm">{m.unit}</TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">{fmtDate(m.last_date)}</TableCell>
                  <TableCell className="text-right num font-semibold">{fmt(m.computed_price)} Kč</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {data.total > 400 && <p className="text-xs text-muted-foreground">Zobrazeno prvních 400 z {data.total}. Použijte hledání.</p>}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <LineChartIcon className="h-5 w-5 text-primary" /> {detail?.name}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <Tabs defaultValue="prices">
              <TabsList data-testid="material-detail-tabs">
                <TabsTrigger value="prices" data-testid="material-tab-prices">Ceny a příjmy</TabsTrigger>
                <TabsTrigger value="nutrition" data-testid="material-tab-nutrition">Výživové hodnoty & alergeny</TabsTrigger>
              </TabsList>
              <TabsContent value="prices">
                <MaterialDetail detail={detail} />
              </TabsContent>
              <TabsContent value="nutrition" className="pt-2">
                <NutritionEditor code={detail.code} />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MaterialDetail({ detail }) {
  const chartData = detail.records
    .filter((r) => r.receipt_date)
    .map((r) => ({ date: r.receipt_date, netto: r.price_netto, brutto: r.price_brutto, supplier: r.supplier }))
    .sort((a, b) => (a.date > b.date ? 1 : -1));

  return (
    <div className="space-y-5">
      <div className="text-sm text-muted-foreground">
        Kód {detail.code} · {detail.records.length} příjmů · dodavatelé: {detail.suppliers.length}
      </div>
      <div className="h-64 bg-muted/30 rounded-lg p-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E2E0" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Line type="monotone" dataKey="netto" stroke="#5E7A60" strokeWidth={2} dot={false} name="Netto" />
            <Line type="monotone" dataKey="brutto" stroke="#C27D65" strokeWidth={2} dot={false} name="Brutto" />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="max-h-52 overflow-auto border border-border rounded-lg">
        <Table>
          <TableHeader><TableRow className="bg-muted/60">
            <TableHead>Datum</TableHead><TableHead>Dodavatel</TableHead>
            <TableHead className="text-right">Netto/MJ</TableHead><TableHead className="text-right">Brutto/MJ</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {[...detail.records].reverse().map((r) => (
              <TableRow key={r.id}>
                <TableCell className="text-sm">{fmtDate(r.receipt_date)}</TableCell>
                <TableCell className="text-sm">{r.supplier}</TableCell>
                <TableCell className="text-right num">{fmt(r.price_netto)}</TableCell>
                <TableCell className="text-right num text-muted-foreground">{fmt(r.price_brutto)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
